// Search whatever script the learner's language is written in.
//
// translate.ts is the English search — spaced words, IDF weighting, contractions expanded — and it
// is right for the two courses whose learner reads English. It cannot be the search for the other
// two: its normaliser strips every non-ASCII character, so on en-zh and en-ja, whose learner types
// Chinese or Japanese, it builds an index out of nothing and answers every phrase with silence
// (en-zh indexed 38 of its 8,386 sentences). This routes each corpus to the search its script needs
// (MYN-220).
//
// WHY THE FIX IS NOT IN translate.ts. The corpus generators import that file, and the drift guard
// (test/drift.test.ts) fingerprints the import — a change there asks for a corpus rebuild. The
// vendor inputs those corpora were last built from have since moved on, so the rebuild would change
// which sentences ship and pull the recordings along with it; none of that belongs in a search fix.
// So the English search stays exactly as it is and is used, unchanged, for the courses it fits, and
// the character search a run-together language needs sits beside it here.
//
// The CJK search is deliberately simpler than the English one: no IDF weighting or common-word
// preference (both are facts about the English corpus), just the two guards that keep a closed
// corpus honest — every character asked for must be present, and the sentence must not say much more
// than was asked — plus the negation check, because a learner who cannot read the answer cannot tell
// an affirmative from its opposite.

import {
  buildIndex as buildEnglishIndex,
  lookupWords as lookupEnglishWords,
  search as searchEnglish,
  MIN_PRECISION,
  type Index as EnglishIndex,
  type Match,
  type WordMatch,
} from './translate.js';
import type { Sentence, Word } from './types.js';

export type { Match, WordMatch };

/** Han (incl. extension A and compatibility) plus hiragana and katakana — a run-together script. */
const CJK_CHAR = /[㐀-鿿豈-﫿぀-ヿ]/u;
const hasCJK = (text: string): boolean => CJK_CHAR.test(text);

/** Characters and endings that flip a CJK sentence's meaning; see the note in translate.ts on why
 *  negation is disqualifying. Found as a substring — the marks do not stand alone as words — and
 *  erring toward yes (汚い "dirty" ends in ない and is caught), which withholds a result rather than
 *  ever returning the opposite meaning. */
const CJK_NEGATION = ['不', '没', '沒', '無', '无', 'ない', 'ません', 'なかった', 'ぬ', 'じゃない', 'ではない'];

/** The gapless comparable form — the exact-match key and what negation is read from. */
function cjkNormalise(text: string): string {
  return [...text.toLowerCase()].filter((c) => CJK_CHAR.test(c) || /[a-z0-9]/u.test(c)).join('');
}

/** Each CJK character a term of its own; a run of ASCII letters or digits kept whole. */
function cjkTokens(text: string): string[] {
  return [...(text.toLowerCase().match(/[㐀-鿿豈-﫿぀-ヿ]|[a-z0-9]+/gu) ?? [])];
}

function isNegated(norm: string): boolean {
  return CJK_NEGATION.some((neg) => norm.includes(neg));
}

interface CjkDoc {
  at: number;
  terms: Set<string>;
  norm: string;
}
interface CjkIndex {
  cjk: true;
  exact: Map<string, number>;
  docs: CjkDoc[];
}
export type Index = EnglishIndex | CjkIndex;

/** Whether a corpus's known-language side (what a learner types and what is matched) is CJK. */
function knownIsCJK(sentences: Sentence[]): boolean {
  const first = sentences.find((s) => s.en);
  return first ? hasCJK(first.en) : false;
}

export function buildIndex(sentences: Sentence[]): Index {
  if (!knownIsCJK(sentences)) return buildEnglishIndex(sentences);
  const exact = new Map<string, number>();
  const docs: CjkDoc[] = [];
  sentences.forEach((s, at) => {
    const norm = cjkNormalise(s.en);
    if (norm && !exact.has(norm)) exact.set(norm, at);
    docs.push({ at, terms: new Set(cjkTokens(s.en)), norm });
  });
  return { cjk: true, exact, docs };
}

export function search(index: Index, sentences: Sentence[], query: string, limit = 5): Match[] {
  if (!('cjk' in index)) return searchEnglish(index, sentences, query, limit);

  const asked = new Set(cjkTokens(query));
  if (!asked.size) return [];
  const askedNegated = isNegated(cjkNormalise(query));

  const out: Match[] = [];
  const seen = new Set<number>();
  const exactAt = index.exact.get(cjkNormalise(query));
  if (exactAt !== undefined) {
    out.push({ at: exactAt, score: Infinity, exact: true });
    seen.add(exactAt);
  }

  const scored: Match[] = [];
  for (const doc of index.docs) {
    if (seen.has(doc.at)) continue;
    // The opposite meaning is worse than no answer, so a mismatch in polarity is disqualifying.
    if (isNegated(doc.norm) !== askedNegated) continue;
    let missing = 0;
    for (const t of asked) if (!doc.terms.has(t)) missing++;
    if (missing > 0) continue;
    // How much of the sentence is answer rather than extra — the same floor the English search uses.
    const precision = asked.size / Math.max(asked.size, doc.terms.size);
    if (precision < MIN_PRECISION) continue;
    scored.push({ at: doc.at, score: precision, exact: false });
  }
  // Best precision first; among equals the shorter sentence, then corpus order for stability.
  scored.sort(
    (a, b) => b.score - a.score || sentences[a.at]!.w.length - sentences[b.at]!.w.length || a.at - b.at,
  );
  return [...out, ...scored].slice(0, limit);
}

export function lookupWords(words: Word[], query: string, limit = 8): WordMatch[] {
  if (!hasCJK(query)) return lookupEnglishWords(words, query, limit);
  const q = cjkNormalise(query);
  if (!q) return [];
  const out: WordMatch[] = [];
  words.forEach((word, at) => {
    for (const gloss of word.e) {
      // Glosses arrive semicolon- or comma-joined and may carry a parenthetical note; compare each
      // sense on its own, the way the English lookup does.
      for (const raw of gloss.replace(/\([^)]*\)/gu, ' ').split(/[;,、，]/u)) {
        const sense = raw.trim();
        if (!sense) continue;
        if (cjkNormalise(sense) === q) {
          out.push({ at, sense, exact: true });
          return;
        }
      }
    }
  });
  // Commonest first: a learner asking how to say a word wants the word people actually use.
  return out.sort((a, b) => words[a.at]!.r - words[b.at]!.r).slice(0, limit);
}
